import { MirrorsAndCameras, DriverSettings, DEFAULT_DRIVER_SETTINGS } from '../vehicle/MirrorsAndCameras';
import { SettingsSyncService } from '../core/SettingsSync';

export class CockpitAdjuster {
  public container: HTMLElement;
  private panel: HTMLElement;
  private isOpenState: boolean = false;
  private sliderInputs: Map<keyof DriverSettings, { slider: HTMLInputElement; valLabel: HTMLElement; unit: string }> = new Map();
  private onSettingChangedCallback: ((settings: DriverSettings) => void) | null = null;
  private onCloseCallback: (() => void) | null = null;

  constructor(private mirrorsAndCameras: MirrorsAndCameras) {
    this.container = document.createElement('div');
    this.container.id = 'cockpit-adjuster-container';
    this.container.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 35;
      display: none;
    `;

    this.panel = document.createElement('div');
    this.panel.id = 'cockpit-adjuster-panel';
    this.panel.style.cssText = `
      position: absolute;
      top: clamp(54px, 10vh, 76px);
      left: clamp(8px, 2vw, 20px);
      width: min(340px, 94vw);
      max-height: 85vh;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
      background: rgba(15, 23, 42, 0.94);
      backdrop-filter: blur(16px);
      border: 1.5px solid #3b82f6;
      border-radius: 16px;
      padding: clamp(12px, 3vw, 16px);
      box-shadow: 0 12px 40px rgba(0, 0, 0, 0.75);
      display: flex;
      flex-direction: column;
      gap: 12px;
      pointer-events: auto;
      transform: translateX(-20px);
      opacity: 0;
      transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease;
      box-sizing: border-box;
    `;

    this.buildUI();
    this.container.appendChild(this.panel);
  }

  private buildUI() {
    // 1. Header
    const header = document.createElement('div');
    header.style.cssText = `display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid rgba(255,255,255,0.1); padding-bottom: 8px;`;

    const titleBox = document.createElement('div');
    titleBox.style.cssText = `display: flex; align-items: center; gap: 8px;`;
    titleBox.innerHTML = `
      <span style="font-size: 18px;">🪑</span>
      <div>
        <div style="font-size: 13px; font-weight: 800; color: #f8fafc; font-family: 'Chakra Petch', sans-serif; letter-spacing: 0.05em;">COCKPIT-JUSTIERUNG</div>
        <div style="font-size: 10px; color: #94a3b8; font-weight: 600;">Sitz & Spiegel live einstellen</div>
      </div>
    `;

    const btnClose = document.createElement('button');
    btnClose.innerText = '✕';
    btnClose.title = 'Schließen';
    btnClose.style.cssText = `
      background: rgba(255,255,255,0.08);
      border: 1px solid rgba(255,255,255,0.2);
      color: #f8fafc;
      border-radius: 50%;
      width: 44px;
      height: 44px;
      min-width: 44px;
      min-height: 44px;
      font-size: 16px;
      font-weight: bold;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      touch-action: manipulation;
      box-sizing: border-box;
    `;
    btnClose.addEventListener('click', () => this.close());

    header.appendChild(titleBox);
    header.appendChild(btnClose);
    this.panel.appendChild(header);

    // 1b. Quick Cockpit Presets (Fahrsimulator 2013 / Fahrersitz / Motorhaube)
    const presetsRow = document.createElement('div');
    presetsRow.style.cssText = `display: flex; gap: 6px; overflow-x: auto; padding: 2px 0 6px; scrollbar-width: none; border-bottom: 1px solid rgba(255,255,255,0.06);`;

    const createPresetBtn = (label: string, icon: string, presetKey: 'citycardriving' | 'fahrsimulator' | 'hood' | 'sport') => {
      const btn = document.createElement('button');
      btn.innerHTML = `${icon} <span style="font-size:11px;font-weight:700;">${label}</span>`;
      btn.style.cssText = `
        background: rgba(30, 41, 59, 0.8);
        border: 1px solid rgba(59, 130, 246, 0.4);
        color: #93c5fd;
        padding: 6px 10px;
        border-radius: 8px;
        cursor: pointer;
        white-space: nowrap;
        font-family: 'Chakra Petch', sans-serif;
        display: flex;
        align-items: center;
        gap: 4px;
        transition: all 0.15s;
        touch-action: manipulation;
      `;
      btn.addEventListener('click', () => {
        this.mirrorsAndCameras.setDriverPreset(presetKey);
        SettingsSyncService.getInstance().updateDriverSettings(this.mirrorsAndCameras.driverSettings);
        this.refreshValues();
        if (this.onSettingChangedCallback) {
          this.onSettingChangedCallback(this.mirrorsAndCameras.driverSettings);
        }
      });
      return btn;
    };

    presetsRow.appendChild(createPresetBtn('Fahrersitz', '🪑', 'citycardriving'));
    presetsRow.appendChild(createPresetBtn('Fahrsimulator 2013', '🚗', 'fahrsimulator'));
    presetsRow.appendChild(createPresetBtn('Motorhaube', '🎯', 'hood'));
    presetsRow.appendChild(createPresetBtn('Sport', '🏎️', 'sport'));
    this.panel.appendChild(presetsRow);

    // 2. Sliders
    const slidersList = document.createElement('div');
    slidersList.style.cssText = `display: flex; flex-direction: column; gap: 8px; max-height: 55vh; overflow-y: auto; padding-right: 2px;`;

    const sliderConfigs: { key: keyof DriverSettings; label: string; min: number; max: number; step: number; unit: string }[] = [
      { key: 'seatHeightOffset', label: '🪑 Sitzhöhe', min: -0.15, max: 0.15, step: 0.01, unit: 'cm' },
      { key: 'seatDepthOffset', label: '↔️ Sitztiefe (Pedalabstand)', min: -0.25, max: 0.15, step: 0.01, unit: 'cm' },
      { key: 'mirrorInteriorAngle', label: '🔍 Innenspiegel Neigung', min: -10, max: 10, step: 1, unit: '°' },
      { key: 'mirrorLeftAngle', label: '◀️ Außenspiegel Links', min: -20, max: 20, step: 1, unit: '°' },
      { key: 'mirrorRightAngle', label: '▶️ Außenspiegel Rechts', min: -20, max: 20, step: 1, unit: '°' },
    ];

    const currentSettings = this.mirrorsAndCameras.driverSettings;

    sliderConfigs.forEach(({ key, label, min, max, step, unit }) => {
      const row = document.createElement('div');
      row.style.cssText = `
        background: rgba(30, 41, 59, 0.7);
        border: 1px solid rgba(51, 65, 85, 0.8);
        border-radius: 8px;
        padding: 8px 10px;
      `;

      const topRow = document.createElement('div');
      topRow.style.cssText = `display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px;`;

      const lbl = document.createElement('span');
      lbl.style.cssText = `font-size: 11px; color: #e2e8f0; font-weight: 600;`;
      lbl.innerText = label;

      const rawVal = currentSettings[key];
      const displayVal = unit === 'cm' ? `${Math.round(rawVal * 100)} cm` : `${rawVal}°`;

      const valLabel = document.createElement('span');
      valLabel.style.cssText = `font-size: 11px; color: #38bdf8; font-weight: 700; font-family: monospace; min-width: 48px; text-align: right;`;
      valLabel.innerText = displayVal;

      topRow.appendChild(lbl);
      topRow.appendChild(valLabel);

      const slider = document.createElement('input');
      slider.type = 'range';
      slider.min = String(min);
      slider.max = String(max);
      slider.step = String(step);
      slider.value = String(rawVal);
      slider.style.cssText = `
        width: 100%;
        accent-color: #3b82f6;
        cursor: pointer;
        height: 28px;
        margin: 4px 0 2px;
        touch-action: pan-y;
      `;

      slider.addEventListener('input', () => {
        const v = parseFloat(slider.value);
        this.mirrorsAndCameras.setDriverSetting(key, v);
        SettingsSyncService.getInstance().updateDriverSettings(this.mirrorsAndCameras.driverSettings);
        if (unit === 'cm') {
          valLabel.innerText = `${Math.round(v * 100)} cm`;
        } else {
          valLabel.innerText = `${v}°`;
        }
        if (this.onSettingChangedCallback) {
          this.onSettingChangedCallback(this.mirrorsAndCameras.driverSettings);
        }
      });

      row.appendChild(topRow);
      row.appendChild(slider);
      slidersList.appendChild(row);

      this.sliderInputs.set(key, { slider, valLabel, unit });
    });

    this.panel.appendChild(slidersList);

    // 3. Actions / Footer
    const footer = document.createElement('div');
    footer.style.cssText = `display: flex; gap: 8px; justify-content: space-between; align-items: center; border-top: 1px solid rgba(255,255,255,0.1); padding-top: 10px;`;

    const btnReset = document.createElement('button');
    btnReset.innerText = '↺ Standard';
    btnReset.title = 'Auf Werkseinstellungen zurücksetzen';
    btnReset.style.cssText = `
      background: rgba(51, 65, 85, 0.6);
      border: 1px solid #475569;
      color: #cbd5e1;
      padding: 10px 14px;
      min-height: 44px;
      border-radius: 10px;
      font-size: 12px;
      font-weight: 700;
      cursor: pointer;
      touch-action: manipulation;
      box-sizing: border-box;
      display: flex;
      align-items: center;
      justify-content: center;
    `;
    btnReset.addEventListener('click', () => this.resetToDefaults());

    const btnDone = document.createElement('button');
    btnDone.innerText = '✓ Fertig';
    btnDone.style.cssText = `
      background: #2563eb;
      border: 1px solid #3b82f6;
      color: white;
      padding: 10px 18px;
      min-height: 44px;
      border-radius: 10px;
      font-size: 13px;
      font-weight: 700;
      cursor: pointer;
      touch-action: manipulation;
      box-shadow: 0 2px 8px rgba(37,99,235,0.4);
      box-sizing: border-box;
      display: flex;
      align-items: center;
      justify-content: center;
    `;
    btnDone.addEventListener('click', () => this.close());

    footer.appendChild(btnReset);
    footer.appendChild(btnDone);
    this.panel.appendChild(footer);
  }

  public refreshValues() {
    const ds = this.mirrorsAndCameras.driverSettings;
    this.sliderInputs.forEach(({ slider, valLabel, unit }, key) => {
      const rawVal = ds[key];
      slider.value = String(rawVal);
      if (unit === 'cm') {
        valLabel.innerText = `${Math.round(rawVal * 100)} cm`;
      } else {
        valLabel.innerText = `${rawVal}°`;
      }
    });
  }

  public resetToDefaults() {
    this.mirrorsAndCameras.resetDriverSettings();
    SettingsSyncService.getInstance().updateDriverSettings(this.mirrorsAndCameras.driverSettings);
    this.refreshValues();
    if (this.onSettingChangedCallback) {
      this.onSettingChangedCallback(this.mirrorsAndCameras.driverSettings);
    }
  }

  public open() {
    this.refreshValues();
    this.container.style.display = 'block';
    this.isOpenState = true;
    requestAnimationFrame(() => {
      this.panel.style.transform = 'translateX(0)';
      this.panel.style.opacity = '1';
    });
  }

  public close() {
    this.isOpenState = false;
    this.panel.style.transform = 'translateX(-20px)';
    this.panel.style.opacity = '0';
    setTimeout(() => {
      if (!this.isOpenState) {
        this.container.style.display = 'none';
      }
    }, 200);

    if (this.onCloseCallback) {
      this.onCloseCallback();
    }
  }

  public toggle() {
    if (this.isOpenState) {
      this.close();
    } else {
      this.open();
    }
  }

  public isOpen(): boolean {
    return this.isOpenState;
  }

  public onSettingChanged(cb: (settings: DriverSettings) => void) {
    this.onSettingChangedCallback = cb;
  }

  public onClose(cb: () => void) {
    this.onCloseCallback = cb;
  }

  public mount(root: HTMLElement) {
    root.appendChild(this.container);
  }
}
